import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, errMsg, fmtDate, ghsOnly as cedi } from "../../lib/api";
import { Field } from "../../components/common";

const IMAGES = [["/brand/belt_yellow.jpg", "Yellow Belt photo"], ["/brand/belt_green.jpg", "Green Belt class photo"], ["/brand/belt_black.jpg", "Black Belt photo"], ["/brand/team_training.jpg", "Team training photo"], ["/brand/hero.jpg", "Office photo"]];
const BLANK = { title: "", start_date: "", end_date: "", schedule: "", mode: "Classroom", venue: "", price: 500, image: IMAGES[0][0], highlight: "" };
const STATUSES = ["new", "contacted", "scheduled", "closed"];

export const TrainingsManager = () => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState(BLANK);
  const load = () => api.get("/admin/trainings").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    try { await api.post("/admin/trainings", { ...f, price: Number(f.price) || 0 }); toast.success("Training published to the home page"); setF(BLANK); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => { await api.delete(`/admin/trainings/${id}`); load(); };

  return (
    <div className="grid lg:grid-cols-12 gap-5">
      <div className="lg:col-span-5 dx-card p-6 space-y-3" data-testid="admin-training-form">
        <h3 className="font-display font-semibold text-lg">Publish an upcoming training</h3>
        <Field label="Title"><input className="dx-input" value={f.title} onChange={set("title")} placeholder="Certified Six Sigma Black Belt (CSSBB)" data-testid="atr-title-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start date"><input type="date" className="dx-input" value={f.start_date} onChange={set("start_date")} data-testid="atr-start-input" /></Field>
          <Field label="End date"><input type="date" className="dx-input" value={f.end_date} onChange={set("end_date")} data-testid="atr-end-input" /></Field>
        </div>
        <Field label="Schedule"><input className="dx-input" value={f.schedule} onChange={set("schedule")} placeholder="Saturdays only, 9am - 4pm" data-testid="atr-schedule-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Mode">
            <select className="dx-input" value={f.mode} onChange={set("mode")} data-testid="atr-mode-select">{["Classroom", "Live virtual sessions", "Hybrid"].map((m) => <option key={m}>{m}</option>)}</select>
          </Field>
          <Field label="Price (GH₵)"><input type="number" className="dx-input" value={f.price} onChange={set("price")} data-testid="atr-price-input" /></Field>
        </div>
        <Field label="Venue"><input className="dx-input" value={f.venue} onChange={set("venue")} placeholder="Accra / Zoom" data-testid="atr-venue-input" /></Field>
        <Field label="Badge text (optional)"><input className="dx-input" value={f.highlight} onChange={set("highlight")} placeholder="Early bird" data-testid="atr-highlight-input" /></Field>
        <Field label="Image">
          <select className="dx-input" value={f.image} onChange={set("image")} data-testid="atr-image-select">{IMAGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        </Field>
        <img src={f.image} alt="" className="w-full aspect-[16/9] object-cover rounded-md" />
        <button className="dx-btn-gold w-full" disabled={f.title.length < 2 || !f.start_date} onClick={save} data-testid="atr-save-btn"><Plus className="w-4 h-4" />Publish</button>
      </div>
      <div className="lg:col-span-7 dx-card divide-y" data-testid="admin-trainings-list">
        {rows.length === 0 && <p className="p-6 text-sm text-[#6b5a73]">No trainings published. The home page shows a "dates coming soon" message until you add one.</p>}
        {rows.map((t) => (
          <div key={t.id} className="p-4 flex gap-4 items-center" data-testid={`admin-training-${t.id}`}>
            <img src={t.image} alt="" className="w-24 h-16 object-cover rounded-md" />
            <div className="flex-1 min-w-0"><div className="font-semibold truncate">{t.title}</div><div className="text-xs text-[#6b5a73]">{fmtDate(t.start_date)}{t.end_date ? ` - ${fmtDate(t.end_date)}` : ""} · {t.mode} · {cedi(t.price)}</div></div>
            <button onClick={() => remove(t.id)} aria-label="Delete" data-testid={`admin-training-${t.id}-delete`}><Trash2 className="w-4 h-4 text-slate-400 hover:text-rose-600" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const TrainingRequests = () => {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/training-requests").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const move = async (id, status) => { await api.patch(`/admin/training-requests/${id}`, { status }); toast.success("Request updated"); load(); };

  return (
    <div className="dx-card overflow-x-auto">
      <table className="w-full text-sm" data-testid="admin-treq-table">
        <thead className="bg-[#FAF8FC] text-left text-xs uppercase tracking-wider text-[#8a7a92]">
          <tr>{["Company", "Contact", "Training", "Staff", "Delivery", "Received", "Status"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y">
          {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#6b5a73]">No training requests yet.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} data-testid={`admin-treq-${r.id}`}>
              <td className="px-4 py-3 font-semibold">{r.company_name}{r.message && <div className="text-xs text-[#6b5a73] font-normal max-w-xs">{r.message}</div>}</td>
              <td className="px-4 py-3">{r.contact_name}<div className="text-xs text-[#6b5a73]">{r.phone} · {r.email}</div></td>
              <td className="px-4 py-3">{r.training}</td>
              <td className="px-4 py-3">{r.staff_count}</td>
              <td className="px-4 py-3">{r.mode}{r.preferred_date && <div className="text-xs text-[#6b5a73]">from {fmtDate(r.preferred_date)}</div>}</td>
              <td className="px-4 py-3 whitespace-nowrap">{fmtDate(r.created_at)}</td>
              <td className="px-4 py-3">
                <select className="dx-input py-1.5 text-xs w-auto capitalize" value={r.status} onChange={(e) => move(r.id, e.target.value)} data-testid={`admin-treq-${r.id}-status`}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
